import { Dispatch, SetStateAction } from 'react';
import { SwitchWithText, Text, colors } from '@innovationdepartment/proxima-ui';
import styled from '@emotion/styled';
import { BillingPlanDefinitions, Plan } from 'types/billing';
import SelectedPlan from './SelectedPlan.Container';
import { SubscriptionPlan } from '@innovationdepartment/proxima-sdk-axios';
import { HELLO_EMAIL } from 'constants/emails';

const BillingFrequencyContainer = styled.div`
  border: 1px solid ${colors.neutral10};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
`;

const BillingFrequencySwitchContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 32px;
`;

const renderBillingFrequencySwitch = (
  setBillingFrequency: ToggleStandardPlansViewProps['setBillingFrequency'],
  opts: SelectedSide
) => {
  const { onlyRight, selectedSide } = opts;
  return (
    <BillingFrequencySwitchContainer>
      <SwitchWithText
        defaultSelected={selectedSide}
        onChange={(value) => setBillingFrequency(value as Plan)}
        selectedColors={{ selected: colors.bigBangBlack, unselected: colors.neutral30 }}
        leftSwitch={onlyRight ? undefined : { text: 'Monthly', value: Plan.MonthByMonth }}
        rightSwitch={{ text: 'Yearly', value: Plan.Annual }}
      />
    </BillingFrequencySwitchContainer>
  );
};

type SelectedSide = { selectedSide: 'left' | 'right'; onlyRight: boolean };
type ToggleStandardPlansViewProps = {
  billingFrequency: Plan;
  billingPlanOpts: BillingPlanDefinitions;
  setBillingFrequency: Dispatch<SetStateAction<Plan>>;
};

const PendingChargesWarning = () => (
  <Text variant="body2" color="red40">
    <b>Warning:</b> A payment process is already in progress. Proceeding may result in duplicate
    charges if the payment has already been confirmed on your end. If you have already completed the
    payment, please wait while our systems finalize the processing. For further assistance, contact
    our <a href={`mailto:${HELLO_EMAIL}`}>support team</a>.
  </Text>
);

const ToggleStandardPlansView = ({
  billingFrequency,
  setBillingFrequency,
  billingPlanOpts,
}: ToggleStandardPlansViewProps) => {
  const { isPendingConfirmation } = billingPlanOpts;
  const type =
    billingFrequency === Plan.MonthByMonth
      ? SubscriptionPlan.MonthByMonth
      : SubscriptionPlan.Annual;

  let selectedSide: SelectedSide = { selectedSide: 'left', onlyRight: false };

  const { isMonthly } = billingPlanOpts;

  if (isMonthly) selectedSide = { selectedSide: 'right', onlyRight: true };

  return (
    <>
      {renderBillingFrequencySwitch(setBillingFrequency, selectedSide)}
      <BillingFrequencyContainer>
        {isPendingConfirmation && <PendingChargesWarning />}
        <SelectedPlan type={type} />
      </BillingFrequencyContainer>
    </>
  );
};

export default ToggleStandardPlansView;
